/**
 * Common route patterns shared across multiple APIs
 */

/**
 * Standard REST routes that appear in most controllers
 */
export const REST_ROUTES = {
  ROOT: '',
  GET_BY_ID: ':id',
} as const;

/**
 * Common health/status endpoints
 */
export const HEALTH_ROUTES = {
  ROOT: '',
  HEALTH: 'health',
  STATUS: 'status',
} as const;

/**
 * Helper to document existing camelCase CRUD route patterns
 *
 * WARNING: Do NOT use this for new routes! New routes should use REST conventions
 * with kebab-case. This helper exists solely to document existing camelCase routes.
 *
 * Existing controllers use these camelCase patterns:
 * - get{Resource} / get{Resource}s
 * - get{Resource}ById
 * - add{Resource}
 * - update{Resource}
 * - delete{Resource}
 *
 * @param resourceName - Singular resource name in PascalCase (e.g., 'Role', 'User', 'Designation')
 * @param options - Configuration options
 * @param options.prefix - Prefix for constant names: 'LEGACY_' (true legacy endpoints from cdw-saas-tools) or 'CAMELCASE_' (current non-REST implementation)
 * @param options.pluralName - Optional plural name if different from resourceName + 's'
 * @returns Object with camelCase route constants (for existing routes only)
 *
 * @example
 * ```typescript
 * // For actual legacy endpoints from cdw-saas-tools (controller has comment "// Legacy endpoints")
 * createCamelCaseCrudRoutes('Role', { prefix: 'LEGACY_' });
 * // Returns: { LEGACY_GET: 'getRoles', LEGACY_GET_BY_ID: 'getRoleById', ... }
 *
 * // For current camelCase implementations (no REST version exists yet)
 * createCamelCaseCrudRoutes('Permission', { prefix: 'CAMELCASE_' });
 * // Returns: { CAMELCASE_GET: 'getPermissions', ... }
 * ```
 */
export function createCamelCaseCrudRoutes(
  resourceName: string,
  options: { prefix?: string; pluralName?: string } = {}
) {
  const { prefix = '', pluralName } = options;
  const plural = pluralName || `${resourceName}s`;

  return {
    [`${prefix}GET`]: `get${plural}`,
    [`${prefix}GET_BY_ID`]: `get${resourceName}ById`,
    [`${prefix}ADD`]: `add${resourceName}`,
    [`${prefix}UPDATE`]: `update${resourceName}`,
    [`${prefix}DELETE`]: `delete${resourceName}`,
  } as const;
}

/**
 * Helper to document existing camelCase "get only" route patterns
 *
 * WARNING: Do NOT use this for new routes! New routes should use REST conventions
 * with kebab-case. This helper exists solely to document existing camelCase routes.
 *
 * Some simpler controllers only have a single GET endpoint with camelCase naming
 *
 * @param resourceName - Resource name in PascalCase (e.g., 'Permissions', 'OrgUnits')
 * @param options - Configuration options
 * @param options.prefix - Prefix for constant name: 'LEGACY_' (true legacy endpoint) or 'CAMELCASE_' (current non-REST implementation)
 * @returns Object with camelCase get route constant (for existing routes only)
 *
 * @example
 * ```typescript
 * // For current camelCase implementation (no REST version exists)
 * createCamelCaseGetRoute('Permissions', { prefix: 'CAMELCASE_' });
 * // Returns: { CAMELCASE_GET: 'getPermissions' }
 * ```
 */
export function createCamelCaseGetRoute(
  resourceName: string,
  options: { prefix?: string } = {}
) {
  const { prefix = '' } = options;
  return {
    [`${prefix}GET`]: `get${resourceName}`,
  } as const;
}
